import React, { useState, useEffect } from 'react';
import { ShieldCheck, Building2, Mail, MapPin, UserCheck, AlertTriangle, CheckCircle2, X } from 'lucide-react';

interface KybInvoiceModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const PUBLIC_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com', 'mail.com', 'proton.me', 'protonmail.com'];

export const KybInvoiceModal: React.FC<KybInvoiceModalProps> = ({
  isOpen: externalIsOpen,
  onClose: externalOnClose
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [addonDays, setAddonDays] = useState<number>(0);
  const [formData, setFormData] = useState({
    corporate_email: '',
    legal_company_name: '',
    corporate_address: '',
    signatory_name: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const activeIsOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  const handleClose = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalIsOpen(false);
    }
    setStatus('idle');
    setErrorMessage('');
  };

  useEffect(() => {
    const handleOpenEvent = () => {
      const storedDays = parseInt(sessionStorage.getItem('pending_sprint_addon_days') || '0', 10);
      setAddonDays(storedDays);
      setInternalIsOpen(true);
    };

    window.addEventListener('open-intake-modal', handleOpenEvent);
    return () => window.removeEventListener('open-intake-modal', handleOpenEvent);
  }, []);

  const basePrice = 5200;
  const addonPricePerDay = 2600;
  const totalAmount = basePrice + addonDays * addonPricePerDay;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setStatus('submitting');

    const emailDomain = formData.corporate_email.split('@')[1]?.toLowerCase() || '';
    if (PUBLIC_DOMAINS.includes(emailDomain)) {
      setStatus('error');
      setErrorMessage('Corporate email domain required. Personal providers (@gmail.com, @yahoo.com) are not eligible for net-terms invoicing.');
      return;
    }

    try {
      const res = await fetch('/api/checkout/invoice-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          addon_days: addonDays
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
        setSuccessMessage(data.message || `KYB Qualification cleared. SLA envelope dispatched to ${formData.signatory_name}.`);
      } else {
        setStatus('error');
        setErrorMessage(data.error || 'KYB qualification check failed. Please check corporate details and try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Network error during corporate qualification. Please check your connection.');
    }
  };

  if (!activeIsOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
      <div className="bg-brand-charcoal border-2 border-brand-burgundy rounded-sm max-w-xl w-full p-6 md:p-8 text-brand-sand shadow-2xl relative">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-stone-400 hover:text-white transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-espresso border border-brand-burgundy rounded-sm font-mono text-[10px] uppercase text-brand-orange font-bold tracking-wider mb-3">
            <ShieldCheck className="w-3.5 h-3.5" /> Corporate KYB & SLA Qualification
          </div>
          <h2 className="text-2xl font-bold font-display text-white">
            AI Strategy Sprint Procurement
          </h2>
          <p className="text-xs text-stone-400 font-light mt-1">
            Net-0 Accounts Payable invoicing requires corporate entity verification and an executed SLA.
          </p>
        </div>

        {/* Contract Scope Summary Card */}
        <div className="bg-brand-espresso/80 border border-brand-sand/20 p-4 rounded-sm mb-6 font-mono text-xs space-y-2">
          <div className="flex justify-between text-stone-300">
            <span>Base 2-Day Strategy Sprint:</span>
            <span className="font-bold text-white">$5,200</span>
          </div>
          {addonDays > 0 && (
            <div className="flex justify-between text-brand-orange">
              <span>Milestone Add-On ({addonDays} day{addonDays > 1 ? 's' : ''} @ $2,600/day):</span>
              <span className="font-bold">+ ${(addonDays * addonPricePerDay).toLocaleString()}</span>
            </div>
          )}
          <div className="pt-2 border-t border-brand-sand/20 flex justify-between text-sm font-bold text-white">
            <span>Total Contract Scope:</span>
            <span className="text-brand-orange">${totalAmount.toLocaleString()}</span>
          </div>
        </div>

        {status === 'success' ? (
          <div className="bg-emerald-950/60 border border-emerald-500/40 p-6 rounded-sm text-center space-y-4 font-mono text-xs">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h3 className="text-lg font-bold text-white font-display">KYB Qualification Cleared</h3>
            <p className="text-stone-300 leading-relaxed">{successMessage}</p>
            <div className="pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="bg-brand-burgundy hover:bg-brand-orange text-white font-mono text-xs font-bold px-6 py-2.5 rounded-sm transition-colors cursor-pointer"
              >
                Close & Return
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 font-mono text-xs">
            {status === 'error' && (
              <div className="bg-red-950/60 border border-red-500/40 p-3 rounded-sm text-red-300 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div>
              <label className="block text-stone-300 font-bold uppercase tracking-wider mb-1">
                Corporate Email (Must be corporate domain) *
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-stone-500 absolute left-3 top-3" />
                <input
                  type="email"
                  required
                  placeholder="signatory@yourcompany.com"
                  value={formData.corporate_email}
                  onChange={(e) => setFormData({ ...formData, corporate_email: e.target.value })}
                  className="w-full bg-black/50 border border-brand-sand/30 focus:border-brand-orange rounded-sm pl-9 pr-3 py-2.5 text-white outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-stone-300 font-bold uppercase tracking-wider mb-1">
                Legal Company Name *
              </label>
              <div className="relative">
                <Building2 className="w-4 h-4 text-stone-500 absolute left-3 top-3" />
                <input
                  type="text"
                  required
                  placeholder="Acme Systems Inc"
                  value={formData.legal_company_name}
                  onChange={(e) => setFormData({ ...formData, legal_company_name: e.target.value })}
                  className="w-full bg-black/50 border border-brand-sand/30 focus:border-brand-orange rounded-sm pl-9 pr-3 py-2.5 text-white outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-stone-300 font-bold uppercase tracking-wider mb-1">
                Corporate HQ Address *
              </label>
              <div className="relative">
                <MapPin className="w-4 h-4 text-stone-500 absolute left-3 top-3" />
                <input
                  type="text"
                  required
                  placeholder="100 Innovation Way, Suite 400, Austin TX 78701"
                  value={formData.corporate_address}
                  onChange={(e) => setFormData({ ...formData, corporate_address: e.target.value })}
                  className="w-full bg-black/50 border border-brand-sand/30 focus:border-brand-orange rounded-sm pl-9 pr-3 py-2.5 text-white outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-stone-300 font-bold uppercase tracking-wider mb-1">
                Authorized Signatory Name *
              </label>
              <div className="relative">
                <UserCheck className="w-4 h-4 text-stone-500 absolute left-3 top-3" />
                <input
                  type="text"
                  required
                  placeholder="Jane Doe (CTO / VP Architecture)"
                  value={formData.signatory_name}
                  onChange={(e) => setFormData({ ...formData, signatory_name: e.target.value })}
                  className="w-full bg-black/50 border border-brand-sand/30 focus:border-brand-orange rounded-sm pl-9 pr-3 py-2.5 text-white outline-none transition-colors"
                />
              </div>
            </div>

            <div className="pt-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2.5 border border-stone-600 hover:border-stone-400 text-stone-300 rounded-sm transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="bg-brand-burgundy hover:bg-brand-orange text-white font-bold px-6 py-2.5 rounded-sm transition-colors cursor-pointer flex items-center gap-2"
              >
                {status === 'submitting' ? 'Running KYB Verification...' : `Request Net-0 Invoice ($${totalAmount.toLocaleString()}) →`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default KybInvoiceModal;
